/**
 * In-memory tables persisted to IndexedDB, synchronised across tabs with
 * BroadcastChannel. This is the prototype's "server". Every repository
 * mutates through `commit()` so persistence, audit and realtime fan-out stay
 * consistent — mirroring a Postgres transaction followed by a Realtime event.
 */
import type {
  AccessCode, Account, AdminNomination, AuditEvent, ChangeRequest, DuplicateSuggestion, Family, FamilyEvent, FamilyMembership, Invitation,
  FamilyPhoto, FeedbackReport, Location, MediaAsset, Notification, PendingOperation, Person, PersonAccountLink, PersonName, PersonPlace, PersonPortrait, PersonStar, ProfileManagementGrant,
  PersonApproval, PersonRemovalProposal, Recipe, RecordingDraft, Relationship, Role, Session, Story, StoryContribution, StoryEvent, StoryPerson, StoryQuestion, StorySuggestion, TemporaryAccessSession,
  Transcript, TreeSettings, UploadSession, UUID,
} from '../../domain/types';
import { DEFAULT_GOVERNANCE, type Governance } from '../../domain/types';
import { idbAvailable, idbClearAll, idbGet, idbSet, idbSetMany } from '../../lib/idb';
import { emit, emitLocal, isLocal, subscribe, type BusMessage } from '../../lib/bus';
import { byId, groupedBy, pileOf } from '../../lib/grouped';
import { uuid } from '../../lib/uuid';
import { nowISO, sleep } from '../../lib/time';
import type { SimulationSettings } from '../types';
import { SharedStore, isShared, sharedStoreEnabled } from './sharedStore';

/** Whose name a name row is. It never changes hands, so it is safe to sort the live table by (see lib/grouped). */
const nameOwner = (n: PersonName) => n.personId;

export interface Tables {
  families: Family[];
  persons: Person[];
  person_names: PersonName[];
  relationships: Relationship[];
  accounts: Account[];
  family_memberships: FamilyMembership[];
  person_account_links: PersonAccountLink[];
  person_portraits: PersonPortrait[];
  family_photos: FamilyPhoto[];
  person_stars: PersonStar[];
  person_places: PersonPlace[];
  person_approvals: PersonApproval[];
  person_removals: PersonRemovalProposal[];
  admin_nominations: AdminNomination[];
  tree_settings: TreeSettings[];
  profile_management_grants: ProfileManagementGrant[];
  access_codes: AccessCode[];
  invitations: Invitation[];
  temporary_access_sessions: TemporaryAccessSession[];
  stories: Story[];
  story_contributions: StoryContribution[];
  story_people: StoryPerson[];
  events: FamilyEvent[];
  story_events: StoryEvent[];
  story_questions: StoryQuestion[];
  /** Changes the family suggested to a story, for its teller to accept or decline. */
  story_suggestions: StorySuggestion[];
  /** The family's recipes, each under the circle of whoever cooks it. */
  recipes: Recipe[];
  locations: Location[];
  media_assets: MediaAsset[];
  upload_sessions: UploadSession[];
  transcripts: Transcript[];
  change_requests: ChangeRequest[];
  duplicate_suggestions: DuplicateSuggestion[];
  audit_events: AuditEvent[];
  notifications: Notification[];
  recording_drafts: RecordingDraft[];
  /** Faults, ideas and questions the family sent through the assistant. */
  feedback_reports: FeedbackReport[];
  pending_operations: PendingOperation[];
  idempotency: Array<{ key: string; entityType: string; entityId: string; at: string }>;
}

export type TableName = keyof Tables;

export const TABLE_NAMES: TableName[] = [
  'families', 'persons', 'person_names', 'relationships', 'accounts', 'family_memberships', 'person_account_links', 'person_portraits', 'family_photos', 'person_stars', 'person_places', 'person_approvals', 'person_removals', 'admin_nominations', 'tree_settings',
  'profile_management_grants', 'access_codes', 'invitations', 'temporary_access_sessions', 'stories', 'story_contributions',
  'story_people', 'events', 'story_events', 'story_questions', 'story_suggestions', 'recipes', 'locations', 'media_assets', 'upload_sessions', 'transcripts', 'change_requests',
  'duplicate_suggestions', 'audit_events', 'notifications', 'recording_drafts', 'feedback_reports', 'pending_operations', 'idempotency',
];

export class NetworkError extends Error {
  retryable = true;
  constructor(message = 'network') {
    super(message);
    this.name = 'NetworkError';
  }
}

const SESSION_KEY = 'edry.session';
const TAB_SESSION_KEY = 'edry.session.tab';
const SIM_KEY = 'edry.proto.simulation';
/**
 * Admin mode: off until an administrator deliberately turns it on, and only on
 * this device. While it is off the session is an ordinary member everywhere —
 * see `freshSession` — so nothing administrative can happen by accident.
 */
const ADMIN_MODE_KEY = 'edry.adminMode';
const SEED_VERSION_KEY = 'edry.seed.version';
export const SEED_VERSION = 6;
/** How long the key of a finished command is kept, in case it is sent again (see `forgetOldRetryKeys`). */
const RETRY_KEY_DAYS = 30;

function emptyTables(): Tables {
  const t = {} as Tables;
  for (const n of TABLE_NAMES) (t as unknown as Record<string, unknown[]>)[n] = [];
  return t;
}

export class MockDb {
  t: Tables = emptyTables();
  ready: Promise<void>;
  private memoryOnly = false;
  private simListeners = new Set<(s: SimulationSettings) => void>();
  private sim: SimulationSettings;
  private seeder: ((db: MockDb) => Promise<void>) | null = null;
  /** Development: the family's rows shared through Supabase (see sharedStore.ts). Null = this browser only. */
  private shared: SharedStore | null = sharedStoreEnabled() ? new SharedStore() : null;
  /** Saves to the shared store go out one at a time, in the order they were made. */
  private pushChain: Promise<void> = Promise.resolve();

  constructor(seeder: (db: MockDb) => Promise<void>) {
    this.seeder = seeder;
    this.sim = this.loadSim();
    this.ready = this.load();
    subscribe((m) => void this.onBus(m));
  }

  // ---- persistence -------------------------------------------------------

  private async load(): Promise<void> {
    if (this.shared) {
      try {
        await this.loadShared(this.shared);
        return;
      } catch (e) {
        console.error('Shared store unavailable — using this browser\'s own copy', e);
        this.shared = null;
      }
    }
    if (!idbAvailable()) {
      this.memoryOnly = true;
      await this.seeder?.(this);
      return;
    }
    // A transient IndexedDB failure at start-up (the browser still closing an old connection,
    // another tab mid-write, a slow disk) used to drop straight into a fresh in-memory demo —
    // which looked like "all the photos are gone" until the next reload. Try a few times first.
    let lastError: unknown;
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        await this.loadFromStorage();
        return;
      } catch (e) {
        lastError = e;
        console.warn(`Local storage read failed (attempt ${attempt + 1} of 4)`, e);
        await sleep(200 * (attempt + 1));
      }
    }
    console.error('IndexedDB unavailable, running in memory only — your saved data is untouched; reload to try again', lastError);
    this.memoryOnly = true;
    this.t = emptyTables();
    await this.seeder?.(this);
  }

  private async loadFromStorage(): Promise<void> {
    const seedVersion = await this.readSeedVersion();
    const loaded: Partial<Tables> = {};
    for (const n of TABLE_NAMES) {
      const rows = await idbGet<unknown[]>('tables', n);
      if (rows) (loaded as Record<string, unknown[]>)[n] = rows;
    }
    if (!loaded.families?.length || seedVersion !== SEED_VERSION) {
      await idbClearAll();
      this.t = emptyTables();
      await this.seeder?.(this);
      await this.persist(TABLE_NAMES);
      await this.writeSeedVersion();
    } else {
      this.t = { ...emptyTables(), ...loaded } as Tables;
      // An older install kept the marker only in localStorage; put a copy beside the data now.
      await this.writeSeedVersion();
      await this.migrate(loaded);
    }
  }

  /**
   * Development: the family comes from the shared store. The first visitor to an
   * empty store seeds it with the demo family. This browser's own tables are
   * kept for what never leaves it (drafts, the offline queue) and as a copy.
   * `migrate` does not run: the shared rows were written by a current build, and
   * an empty table there is the family's doing, not an older install.
   */
  private async loadShared(shared: SharedStore): Promise<void> {
    const remote = await shared.loadAll();
    const local: Partial<Record<TableName, unknown[]>> = {};
    if (idbAvailable()) {
      for (const n of TABLE_NAMES) {
        if (isShared(n)) continue;
        try {
          const rows = await idbGet<unknown[]>('tables', n);
          if (rows) local[n] = rows;
        } catch {
          /* a device-only table that cannot be read starts empty */
        }
      }
    } else {
      this.memoryOnly = true;
    }
    if (remote) {
      this.t = { ...emptyTables(), ...local, ...remote } as Tables;
      await this.writeLocal(TABLE_NAMES);
    } else {
      this.t = emptyTables();
      await this.seeder?.(this);
      await this.persist(TABLE_NAMES);
    }
    await this.writeSeedVersion();
    this.forgetMissingSession();
    shared.listen((tables) => void this.onShared(tables));
  }

  /** The family was reset elsewhere and this device's account is gone: back to the welcome screen. */
  private forgetMissingSession(): void {
    const s = this.getSession();
    if (s && !this.t.accounts.some((a) => a.id === s.accountId)) this.setSession(null);
  }

  /** Another device changed these tables: fetch them and tell the screens. */
  private async onShared(tables: string[]): Promise<void> {
    if (!this.shared) return;
    await this.ready;
    const names = tables.includes('*') ? TABLE_NAMES : (tables.filter((x) => TABLE_NAMES.includes(x as TableName)) as TableName[]);
    try {
      const fresh = await this.shared.fetchTables(names);
      for (const [n, rows] of Object.entries(fresh)) (this.t as unknown as Record<string, unknown[]>)[n] = rows;
      await this.writeLocal(Object.keys(fresh) as TableName[]);
      if ('accounts' in fresh) this.forgetMissingSession();
      // Every tab hears the broadcast itself, so this tab only tells its own screens.
      emitLocal({ type: 'changed', tables: Object.keys(fresh) });
    } catch (e) {
      console.warn('Could not fetch the family\'s latest changes', e);
    }
  }

  /**
   * Which seed this database was built from — **read from beside the data**, and
   * only then from localStorage.
   *
   * This number decides whether the family's whole database is thrown away and
   * rebuilt from the demo seed, so where it is kept matters more than it looks.
   * It used to live only in localStorage while the data lived in IndexedDB: two
   * different stores, evicted and cleared independently, so a browser that let
   * go of localStorage alone would read "version 0", disagree with the build,
   * and wipe every photograph the family had uploaded. Keeping it as a row in
   * the same store as the tables means the two can no longer drift apart. The
   * localStorage copy is still read (so an existing install is recognised rather
   * than reseeded) and still written (so an older build opening this database
   * does not wipe it either).
   */
  private async readSeedVersion(): Promise<number> {
    try {
      const stored = await idbGet<number>('tables', SEED_VERSION_KEY);
      if (typeof stored === 'number' && Number.isFinite(stored)) return stored;
    } catch {
      /* fall back to the older place */
    }
    return Number(localStorage.getItem(SEED_VERSION_KEY) ?? '0');
  }

  private async writeSeedVersion(): Promise<void> {
    try {
      await idbSet('tables', SEED_VERSION_KEY, SEED_VERSION);
    } catch {
      /* the localStorage copy below is then the only marker, as it always was */
    }
    try {
      localStorage.setItem(SEED_VERSION_KEY, String(SEED_VERSION));
    } catch {
      /* ignore */
    }
  }

  /** False when this session could not open the browser's storage and is showing demo data from memory. */
  isPersistent(): boolean {
    return !this.memoryOnly;
  }

  /** One-time upgrades of a local database created by an earlier build. */
  private async migrate(loaded: Partial<Tables>): Promise<void> {
    // World tab: demo homes for the seed family when the places table did not exist yet.
    if (!loaded.person_places) {
      const { seedPlaces } = await import('./seed/places');
      if (seedPlaces(this)) await this.persist(['locations', 'persons', 'person_places']);
      else await this.persist(['person_places']);
    }
    // Round 22: the demo relatives' shared phone numbers and Sarah's question to Miriam, for a
    // database seeded before they existed (nothing the family added is touched).
    // Keyed on the table *and* on the contacts themselves: a database that had already saved
    // `story_questions` (any build of round 22) skipped this and still showed no reach buttons.
    // Round 49 gives most of those relatives an Instagram or a Facebook too, which an
    // install with complete contacts would otherwise never see — so it is asked separately.
    const { demoContactsMissing, demoSocialsMissing, seedStayingClose } = await import('./seed/stayingClose');
    if (!loaded.story_questions || demoContactsMissing(this) || demoSocialsMissing(this)) {
      const changed = new Set<TableName>(seedStayingClose(this));
      changed.add('story_questions'); // saving the (possibly empty) table marks this upgrade as done
      await this.persist([...changed]);
    }
    // The demo family's milestones, for a database seeded before they existed. seedMilestones
    // adds only what is missing, so this is safe whether or not the family has simchas of its own.
    const { seedMilestones } = await import('./seed/milestones');
    const added = seedMilestones(this);
    if (added.length) await this.persist(added);
    /*
     * Recipes arrived after most local databases were made. The demo family's
     * kitchen is added once, the first time a database is opened with no recipes
     * table at all — keyed on the table rather than on the rows, so a family that
     * has taken the demo recipes down does not find them back the next morning.
     */
    if (!loaded.recipes) {
      const { seedRecipes } = await import('./seed/recipes');
      seedRecipes(this);
      await this.persist(['recipes']);
    }
    /*
     * A recipe used to keep one picture of its own. Now anybody in the family
     * can add pictures to a recipe and each is an ordinary Moment naming it, so
     * the picture a recipe already had becomes the first of those — a new row
     * pointing at the very same stored image. Nothing is copied, nothing is
     * lost, and being the first it goes on standing for the recipe.
     */
    type OldPhoto = { storageKey: string; mimeType?: string; bytes?: number; width?: number; height?: number };
    const hadPhoto = this.t.recipes.filter((r) => !!(r as { photo?: OldPhoto }).photo?.storageKey);
    if (hadPhoto.length) {
      for (const r of hadPhoto) {
        const old = r as { photo?: OldPhoto };
        const p = old.photo!;
        this.t.family_photos.push({
          id: uuid(), familyId: r.familyId, uploadedByAccountId: r.creatorAccountId, storageKey: p.storageKey,
          mimeType: p.mimeType || 'image/jpeg', bytes: p.bytes, width: p.width || undefined, height: p.height || undefined,
          isEvent: false, addedTo: { kind: 'recipe', id: r.id }, personIds: [], createdAt: r.createdAt,
        });
        delete old.photo;
      }
      await this.persist(['recipes', 'family_photos']);
    }
    /*
     * A picture added to a simcha used to name it with `partOfMomentId`; now
     * every day the family can fill — a simcha, a story, a birth — is named the
     * same way by `addedTo`. Anything saved under the old field is carried over
     * so those pictures stay inside their wedding.
     */
    const legacy = this.t.family_photos.filter((p) => !p.addedTo && typeof (p as { partOfMomentId?: string }).partOfMomentId === 'string');
    if (legacy.length) {
      for (const p of legacy) {
        const old = p as { partOfMomentId?: string };
        p.addedTo = { kind: 'moment', id: old.partOfMomentId! };
        delete old.partOfMomentId;
      }
      await this.persist(['family_photos']);
    }
    /*
     * Stories used to carry an audience ("only these six people"). They do not
     * any more — a published story belongs to the whole family — so a story
     * saved with one is opened up rather than left quietly hidden.
     */
    const restricted = this.t.stories.filter((s) => 'visibility' in s);
    if (restricted.length) {
      for (const s of restricted) delete (s as { visibility?: unknown }).visibility;
      await this.persist(['stories']);
    }
    const draftsWithAudience = this.t.recording_drafts.filter((d) => 'visibility' in d);
    if (draftsWithAudience.length) {
      for (const d of draftsWithAudience) delete (d as { visibility?: unknown }).visibility;
      await this.persist(['recording_drafts']);
    }
    await this.oneStoryOneTelling();
    await this.forgetOldRetryKeys();
  }

  /**
   * The keys that make a retried command harmless are only needed for as long
   * as a retry could still arrive — a dropped connection, an offline edit
   * replayed when the phone finds a signal. They are plumbing, not the family's
   * record: nothing anybody can see is made of them. But one is written for
   * nearly every save, the table is written out whole each time, and it was
   * never emptied — so after a year of use every photograph added was also
   * rewriting tens of thousands of stale keys. Anything older than a month goes,
   * once, when the app opens. (An edit still waiting in the offline queue keeps
   * its key: the queue is the one place a very late retry can come from.)
   */
  private async forgetOldRetryKeys(): Promise<void> {
    const cutoff = new Date(Date.now() - RETRY_KEY_DAYS * 86_400_000).toISOString();
    const waiting = new Set(this.t.pending_operations.map((op) => op.idempotencyKey));
    const kept = this.t.idempotency.filter((k) => k.at >= cutoff || waiting.has(k.key));
    if (kept.length === this.t.idempotency.length) return;
    this.t.idempotency = kept;
    await this.persist(['idempotency']);
  }

  /**
   * A story used to be able to hold several recordings — "Part 1", "Part 2" —
   * and a relative added theirs with **Contribute to this Story**. It is one
   * telling now, and everything else anyone wants to say about that day is a
   * voice note on the day, which the plus on the day's constellation records.
   *
   * So a story saved with more than one recording is not quietly truncated:
   * every recording after the first is moved out to the day as exactly that
   * voice note — the same Moment the plus would have made, pointing at the
   * story through `addedTo`, keeping the audio, whose voice it is and who put
   * it there. It then shows as a node beside the day's photographs instead of
   * disappearing behind the first recording. A part that was typed rather than
   * spoken has no audio to move and is dropped; its words are in the story's
   * own transcript if anywhere.
   */
  private async oneStoryOneTelling(): Promise<void> {
    const byStory = new Map<UUID, StoryContribution[]>();
    for (const c of this.t.story_contributions) {
      if (c.status === 'removed') continue;
      const list = byStory.get(c.storyId);
      if (list) list.push(c);
      else byStory.set(c.storyId, [c]);
    }
    const movedOut = new Set<UUID>();
    const notes: FamilyPhoto[] = [];
    for (const [storyId, list] of byStory) {
      if (list.length < 2) continue;
      const story = this.t.stories.find((s) => s.id === storyId);
      for (const c of [...list].sort((a, b) => a.order - b.order).slice(1)) {
        movedOut.add(c.id);
        const asset = c.mediaAssetId ? this.t.media_assets.find((m) => m.id === c.mediaAssetId) : undefined;
        if (!asset?.storageKey) continue;
        const id = `voice-${c.id}`;
        if (this.t.family_photos.some((p) => p.id === id)) continue;
        notes.push({
          id,
          familyId: story?.familyId ?? asset.familyId,
          uploadedByAccountId: c.uploaderAccountId,
          // The same blob the recording played: nothing is copied and nothing is lost.
          storageKey: asset.storageKey,
          mimeType: asset.mimeType ?? 'audio/webm',
          bytes: asset.sizeBytes,
          durationMs: asset.durationMs,
          isEvent: false,
          addedTo: { kind: 'story', id: storyId },
          takenOn: story?.date,
          personIds: [c.speakerPersonId],
          createdAt: c.createdAt,
        });
      }
    }
    if (!movedOut.size) return;
    this.t.story_contributions = this.t.story_contributions.filter((c) => !movedOut.has(c.id));
    this.t.transcripts = this.t.transcripts.filter((tr) => !movedOut.has(tr.contributionId));
    this.t.family_photos.push(...notes);
    await this.persist(['story_contributions', 'transcripts', 'family_photos']);
  }

  private async persist(tables: TableName[]): Promise<void> {
    await this.writeLocal(tables);
    const shared = this.shared;
    if (shared) {
      const snapshot = Object.fromEntries(tables.map((n) => [n, this.t[n] as unknown[]]));
      // Not awaited: a save is done once it is on this device, and the family
      // hears about it a moment later. Chained, so saves reach the server in order.
      this.pushChain = this.pushChain
        .then(() => shared.push(snapshot))
        .catch((e) => console.warn('A change could not be shared with the family yet', e));
    }
  }

  private async writeLocal(tables: TableName[]): Promise<void> {
    if (this.memoryOnly) return;
    await idbSetMany('tables', tables.map((n) => [n, this.t[n]]));
  }

  private async reload(tables: TableName[]): Promise<void> {
    if (this.memoryOnly) return;
    for (const n of tables) {
      const rows = await idbGet<unknown[]>('tables', n);
      if (rows) (this.t as unknown as Record<string, unknown[]>)[n] = rows;
    }
  }

  private async onBus(m: BusMessage): Promise<void> {
    if (isLocal(m)) return;
    await this.ready;
    if (m.type === 'changed') {
      const tables = m.tables.includes('*') ? TABLE_NAMES : (m.tables.filter((x) => TABLE_NAMES.includes(x as TableName)) as TableName[]);
      await this.reload(tables);
      emitLocal({ type: 'changed', tables: m.tables, entityIds: m.entityIds });
    } else if (m.type === 'reset') {
      await this.reload(TABLE_NAMES);
      emitLocal({ type: 'reset' });
    } else if (m.type === 'simulation') {
      this.sim = this.loadSim();
      for (const l of this.simListeners) l(this.sim);
    }
  }

  /** Persist the given tables and notify this tab + other tabs. */
  async commit(tables: TableName[], entityIds?: UUID[]): Promise<void> {
    await this.persist(tables);
    emit({ type: 'changed', tables, entityIds });
  }

  async resetAll(): Promise<void> {
    // Shared: this resets the demo family for everybody, not just this browser.
    if (this.shared) await this.shared.clear();
    if (!this.memoryOnly) await idbClearAll();
    this.t = emptyTables();
    await this.seeder?.(this);
    await this.persist(TABLE_NAMES);
    await this.writeSeedVersion();
    emit({ type: 'reset' });
  }

  // ---- session -----------------------------------------------------------

  getSession(): Session | null {
    try {
      const tab = sessionStorage.getItem(TAB_SESSION_KEY);
      if (tab) return JSON.parse(tab) as Session;
      const raw = localStorage.getItem(SESSION_KEY);
      return raw ? (JSON.parse(raw) as Session) : null;
    } catch {
      return null;
    }
  }

  getAdminMode(): boolean {
    try {
      return localStorage.getItem(ADMIN_MODE_KEY) === '1';
    } catch {
      return false;
    }
  }

  setAdminMode(on: boolean): void {
    try {
      if (on) localStorage.setItem(ADMIN_MODE_KEY, '1');
      else localStorage.removeItem(ADMIN_MODE_KEY);
    } catch {
      /* ignore */
    }
  }

  setSession(s: Session | null, thisTabOnly = false): void {
    try {
      if (thisTabOnly) {
        if (s) sessionStorage.setItem(TAB_SESSION_KEY, JSON.stringify(s));
        else sessionStorage.removeItem(TAB_SESSION_KEY);
        return;
      }
      sessionStorage.removeItem(TAB_SESSION_KEY);
      if (s) localStorage.setItem(SESSION_KEY, JSON.stringify(s));
      else {
        localStorage.removeItem(SESSION_KEY);
        localStorage.removeItem(ADMIN_MODE_KEY); // signing out always leaves Admin mode off
      }
    } catch {
      /* ignore */
    }
  }

  requireSession(): Session {
    const s = this.getSession();
    if (!s) throw new Error('not signed in');
    return s;
  }

  // ---- simulation --------------------------------------------------------

  private loadSim(): SimulationSettings {
    const def: SimulationSettings = { offline: false, latencyMs: 250, failureRate: 0, uploadFailures: false, slowUploads: false, shabbatEveryone: false, shabbatPersonIds: [] };
    try {
      const raw = localStorage.getItem(SIM_KEY);
      return raw ? { ...def, ...(JSON.parse(raw) as Partial<SimulationSettings>) } : def;
    } catch {
      return def;
    }
  }

  getSim(): SimulationSettings {
    return this.sim;
  }

  setSim(patch: Partial<SimulationSettings>): void {
    this.sim = { ...this.sim, ...patch };
    try {
      localStorage.setItem(SIM_KEY, JSON.stringify(this.sim));
    } catch { /* ignore */ }
    for (const l of this.simListeners) l(this.sim);
    emit({ type: 'simulation' });
  }

  onSim(l: (s: SimulationSettings) => void): () => void {
    this.simListeners.add(l);
    return () => this.simListeners.delete(l);
  }

  isOffline(): boolean {
    return this.sim.offline || (typeof navigator !== 'undefined' && navigator.onLine === false);
  }

  /** Simulated network for reads: small latency, no failures. */
  async read(): Promise<void> {
    await this.ready;
    if (this.sim.latencyMs > 0) await sleep(Math.min(120, this.sim.latencyMs / 4));
  }

  /** Simulated network for writes: latency, offline and random failure. */
  async write(): Promise<void> {
    await this.ready;
    if (this.isOffline()) throw new NetworkError('offline');
    if (this.sim.latencyMs > 0) await sleep(this.sim.latencyMs);
    if (this.sim.failureRate > 0 && Math.random() < this.sim.failureRate) throw new NetworkError('simulated failure');
  }

  // ---- helpers -----------------------------------------------------------

  idem(key: string): { entityType: string; entityId: string } | undefined {
    return this.t.idempotency.find((x) => x.key === key);
  }

  rememberIdem(key: string, entityType: string, entityId: string): void {
    if (!this.idem(key)) this.t.idempotency.push({ key, entityType, entityId, at: nowISO() });
  }

  audit(e: Omit<AuditEvent, 'id' | 'timestamp' | 'familyId'> & { familyId?: UUID }): AuditEvent {
    const row: AuditEvent = {
      id: uuid(),
      familyId: e.familyId ?? this.familyId(),
      timestamp: nowISO(),
      approvalStatus: 'n/a',
      // The role the actor held at the moment they acted, so "what did the
      // administrators decide?" can be answered without guessing later.
      actorRole: e.actorRole ?? this.roleAtNow(e.actorAccountId ?? null),
      ...e,
    };
    this.t.audit_events.push(row);
    return row;
  }

  /** The role an account is acting with right now (an administrator with Admin mode off is a member). */
  private roleAtNow(accountId: UUID | null): Role | undefined {
    if (!accountId) return undefined;
    const m = this.membership(accountId);
    if (!m) return undefined;
    if (m.role === 'admin' && accountId === this.getSession()?.accountId && !this.getAdminMode()) return 'member';
    return m.role;
  }

  // ---- governance --------------------------------------------------------

  /** Who runs the family and by what numbers — defaults filled in for older stored families. */
  governance(): Governance {
    const fam = this.t.families[0];
    return { ...DEFAULT_GOVERNANCE, ...(fam?.governance ?? {}) };
  }

  /** True when this account's email is on the family's administrator list. */
  isAdminByEmail(accountId: UUID): boolean {
    const email = this.t.accounts.find((a) => a.id === accountId)?.email?.trim().toLowerCase();
    if (!email) return false;
    return this.governance().adminEmails.some((e) => e.trim().toLowerCase() === email);
  }

  /**
   * Administrators are named by email, so the memberships have to follow the
   * list rather than the other way round. Run before any read that depends on
   * the role; returns true when something actually changed.
   */
  syncAdminEmails(): boolean {
    let changed = false;
    for (const m of this.t.family_memberships) {
      if (m.status !== 'active') continue;
      const byEmail = this.isAdminByEmail(m.accountId);
      if (byEmail && m.role !== 'admin') {
        m.role = 'admin';
        changed = true;
      }
    }
    return changed;
  }

  /** Administrator accounts of this family (by membership, which the email list keeps in step). */
  adminAccountIds(): UUID[] {
    this.syncAdminEmails();
    return this.t.family_memberships.filter((m) => m.status === 'active' && m.role === 'admin').map((m) => m.accountId);
  }

  /**
   * Administrators who have actually been present lately — the ones a majority
   * is counted among. Anything they did in the family counts as presence. If
   * nobody qualifies (a long-quiet family) every administrator counts, so a
   * decision is never impossible.
   */
  activeAdminAccountIds(now = Date.now()): UUID[] {
    const all = this.adminAccountIds();
    /*
     * Asked once for every proposal on the Administrators page, on every refresh
     * of it, and each asking used to read the family's whole history — parsing
     * a date out of every line. The answer only changes when the history grows
     * or the administrators do, so it is kept until one of them has; and the
     * timestamps are all written by `nowISO()`, which sorts as text, so nothing
     * needs parsing to be compared.
     */
    const key = `${this.t.audit_events.length}|${this.t.admin_nominations.reduce((n, x) => n + x.votes.length, 0)}|${all.join(',')}|${this.governance().activeAdminDays}|${Math.floor(now / 60_000)}`;
    if (this.activeAdmins?.key === key) return this.activeAdmins.ids;
    const cutoff = new Date(now - this.governance().activeAdminDays * 86_400_000).toISOString();
    const seen = new Set<UUID>();
    for (const ev of this.t.audit_events) {
      if (ev.actorAccountId && ev.timestamp >= cutoff) seen.add(ev.actorAccountId);
    }
    for (const n of this.t.admin_nominations) {
      for (const v of n.votes) if (v.at >= cutoff) seen.add(v.accountId);
    }
    const active = all.filter((id) => seen.has(id));
    const ids = active.length ? active : all;
    this.activeAdmins = { key, ids };
    return ids;
  }
  private activeAdmins: { key: string; ids: UUID[] } | null = null;

  notify(n: Omit<Notification, 'id' | 'read' | 'createdAt'>): void {
    this.t.notifications.push({ id: uuid(), read: false, createdAt: nowISO(), ...n });
  }

  /** One notification for every active member of the family except the person who did it. */
  notifyFamily(exceptAccountId: UUID, n: Omit<Notification, 'id' | 'read' | 'createdAt' | 'accountId'>): void {
    const seen = new Set<UUID>();
    for (const m of this.t.family_memberships) {
      if (m.status !== 'active' || m.accountId === exceptAccountId || seen.has(m.accountId)) continue;
      seen.add(m.accountId);
      this.notify({ accountId: m.accountId, ...n });
    }
  }

  familyId(): UUID {
    return this.t.families[0]?.id ?? 'family';
  }

  /** Accounts that speak for a person: the owner of the circle plus active helpers. */
  accountsForPerson(personId: UUID): UUID[] {
    const out = new Set<UUID>();
    for (const l of this.t.person_account_links) if (l.personId === personId && l.status === 'active') out.add(l.accountId);
    for (const g of this.t.profile_management_grants) if (g.managedPersonId === personId && g.status === 'active') out.add(g.helperAccountId);
    return [...out].filter((id) => this.membership(id));
  }

  accountName(accountId: UUID | null, lang: 'en' | 'he' = 'en'): string {
    if (!accountId) return lang === 'he' ? 'המערכת' : 'System';
    const a = this.t.accounts.find((x) => x.id === accountId);
    return a?.displayName ?? (lang === 'he' ? 'משתמש/ת' : 'Someone');
  }

  /**
   * A person with their names. The names come out of a pile sorted once per
   * table (lib/grouped) rather than being looked for: `people.list()` asks this
   * for everybody in the family on every reload, and "every person walks every
   * name" is a thousand times three thousand. (Whose name a name is never
   * changes, which is what makes that safe on a table edited in place; the pile
   * is copied, so a caller can never reach the one that is kept.)
   */
  personWithNames(id: UUID, row?: Person): (Person & { names: PersonName[] }) | undefined {
    const p = row ?? byId(this.t.persons).get(id);
    if (!p) return undefined;
    return { ...p, names: [...pileOf(groupedBy(this.t.person_names, nameOwner), id)] };
  }

  membership(accountId: UUID): FamilyMembership | undefined {
    return this.t.family_memberships.find((m) => m.accountId === accountId && m.status === 'active');
  }
}
